import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Plus, Trash2, Users, Award, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JugadoresDialog({ club, onClose }) {
  const [jugadores, setJugadores] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [showJugadorForm, setShowJugadorForm] = useState(false);
  const [editingJugador, setEditingJugador] = useState(null);
  const [showCategoriaDialog, setShowCategoriaDialog] = useState(false);
  const [selectedJugador, setSelectedJugador] = useState(null);
  
  // Form jugador
  const [cni, setCni] = useState('');
  const [nombre, setNombre] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  
  // Form categoría
  const [selectedCategoria, setSelectedCategoria] = useState('');

  useEffect(() => {
    loadJugadores();
    loadCategorias();
  }, [club.id]);

  const loadJugadores = async () => {
    try {
      const response = await axios.get(`${API}/clubes/${club.id}/jugadores`, { withCredentials: true });
      setJugadores(response.data || []);
    } catch (error) {
      toast.error('Error al cargar jugadores');
    }
  };

  const loadCategorias = async () => {
    try {
      // Cargar TODAS las categorías de la liga
      const response = await axios.get(`${API}/categorias?liga_id=${club.liga_id}`, { withCredentials: true });
      const todasCategorias = response.data || [];
      
      // Filtrar solo las categorías asociadas al club
      const categoriasClub = club.categorias.map(c => c.categoria_id);
      const categoriasDisponibles = todasCategorias.filter(cat => categoriasClub.includes(cat.id));
      
      setCategorias(categoriasDisponibles);
    } catch (error) {
      console.error('Error al cargar categorías');
    }
  };

  const handleCreateJugador = () => {
    setEditingJugador(null);
    setCni('');
    setNombre('');
    setFechaNacimiento('');
    setShowJugadorForm(true);
  };

  const handleEditJugador = (jugador) => {
    setEditingJugador(jugador);
    setCni(jugador.cni);
    setNombre(jugador.nombre);
    setFechaNacimiento(jugador.fecha_nacimiento);
    setShowJugadorForm(true);
  };

  const handleSaveJugador = async (e) => {
    e.preventDefault();
    try {
      const payload = { cni, nombre, fecha_nacimiento: fechaNacimiento };
      
      if (editingJugador) {
        await axios.put(`${API}/jugadores/${editingJugador.id}`, payload, { withCredentials: true });
        toast.success('Jugador actualizado');
      } else {
        await axios.post(`${API}/clubes/${club.id}/jugadores`, payload, { withCredentials: true });
        toast.success('Jugador creado');
      }
      
      setShowJugadorForm(false);
      loadJugadores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar jugador');
    }
  };

  const handleDeleteJugador = async (jugadorId) => {
    if (!confirm('¿Eliminar este jugador?')) return;
    try {
      await axios.delete(`${API}/jugadores/${jugadorId}`, { withCredentials: true });
      toast.success('Jugador eliminado');
      loadJugadores();
    } catch (error) {
      toast.error('Error al eliminar jugador');
    }
  };

  const handleAgregarCategoria = (jugador) => {
    setSelectedJugador(jugador);
    setSelectedCategoria('');
    setShowCategoriaDialog(true);
  };

  const handleAsociarCategoria = async (e) => {
    e.preventDefault();
    if (!selectedCategoria) return;
    
    try {
      const response = await axios.post(
        `${API}/jugadores/${selectedJugador.id}/categorias`,
        { categoria_id: selectedCategoria },
        { withCredentials: true }
      );
      
      if (response.data.es_excepcion) {
        toast.success(`Categoría agregada como EXCEPCIÓN: ${response.data.validacion}`);
      } else {
        toast.success(`Categoría agregada: ${response.data.validacion}`);
      }
      
      setShowCategoriaDialog(false);
      loadJugadores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar categoría');
    }
  };

  const handleRemoverCategoria = async (jugadorId, jugadorCategoriaId) => {
    if (!confirm('¿Remover esta categoría del jugador?')) return;
    try {
      await axios.delete(`${API}/jugadores/${jugadorId}/categorias/${jugadorCategoriaId}`, { withCredentials: true });
      toast.success('Categoría removida');
      loadJugadores();
    } catch (error) {
      toast.error('Error al remover categoría');
    }
  };

  const getCategoriasDisponibles = (jugador) => {
    const categoriasJugador = jugador.categorias?.map(c => c.categoria_id) || [];
    return categorias.filter(cat => !categoriasJugador.includes(cat.id));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {club.logo ? (
                <img src={club.logo} alt={club.name} className="w-12 h-12 rounded-lg bg-white p-1" />
              ) : (
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold">{club.name}</h2>
                <p className="text-blue-100">Gestión de Jugadores</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Jugadores ({jugadores.length})</h3>
            <Button onClick={handleCreateJugador} className="gap-2">
              <Plus className="w-4 h-4" />
              Nuevo Jugador
            </Button>
          </div>

          {jugadores.length === 0 ? (
            <Card className="p-8 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No hay jugadores registrados</p>
            </Card>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {jugadores.map((jugador) => (
                <Card key={jugador.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">{jugador.nombre}</h4>
                      <div className="text-sm text-slate-600 space-y-1 mt-1">
                        <p>CNI: {jugador.cni}</p>
                        <p>Fecha Nacimiento: {jugador.fecha_nacimiento}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleAgregarCategoria(jugador)} className="gap-2">
                        <Award className="w-4 h-4" />
                        Categorías
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleEditJugador(jugador)}>
                        Editar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteJugador(jugador.id)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {jugador.categorias && jugador.categorias.length > 0 && (
                    <div className="border-t pt-3">
                      <p className="text-xs font-semibold text-slate-600 mb-2">Categorías:</p>
                      <div className="flex flex-wrap gap-2">
                        {jugador.categorias.map((cat) => (
                          <div
                            key={cat.id}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs ${
                              cat.es_excepcion
                                ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                : 'bg-emerald-100 text-emerald-800'
                            }`}
                          >
                            {cat.es_excepcion ? (
                              <AlertCircle className="w-3 h-3" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3" />
                            )}
                            <span className="font-medium">{cat.categoria_nombre}</span>
                            {cat.es_excepcion && <span className="text-[10px]">(Excepción)</span>}
                            <button
                              onClick={() => handleRemoverCategoria(jugador.id, cat.id)}
                              className="ml-1 hover:bg-black/10 rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dialog: Crear/Editar Jugador */}
      <Dialog open={showJugadorForm} onOpenChange={setShowJugadorForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingJugador ? 'Editar Jugador' : 'Nuevo Jugador'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveJugador} className="space-y-4">
            <div>
              <Label>CNI / RUT *</Label>
              <Input
                value={cni}
                onChange={(e) => setCni(e.target.value)}
                required
                placeholder="12.345.678-9"
              />
              <p className="text-xs text-slate-500 mt-1">
                Para RUT chileno, se validará automáticamente
              </p>
            </div>

            <div>
              <Label>Nombre Completo *</Label>
              <Input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                placeholder="Juan Pérez"
              />
            </div>

            <div>
              <Label>Fecha de Nacimiento *</Label>
              <Input
                type="date"
                value={fechaNacimiento}
                onChange={(e) => setFechaNacimiento(e.target.value)}
                required
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowJugadorForm(false)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Agregar Categoría */}
      <Dialog open={showCategoriaDialog} onOpenChange={setShowCategoriaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Categoría - {selectedJugador?.nombre}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAsociarCategoria} className="space-y-4">
            <div>
              <Label>Categoría *</Label>
              <Select value={selectedCategoria} onValueChange={setSelectedCategoria}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar categoría" />
                </SelectTrigger>
                <SelectContent>
                  {getCategoriasDisponibles(selectedJugador || {}).map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                      {cat.tipo_edad && ` (${cat.tipo_edad === 'sub' ? 'Sub' : '≥'} ${cat.valor_edad})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                La validación de edad se realizará automáticamente. Si el jugador no cumple los requisitos pero hay excepciones disponibles, se agregará como excepción.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowCategoriaDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit">Agregar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
